import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import {
  isEventTicketApiConfigured,
  postCheckin,
} from 'services/eventTicketService';

const LS_KEY = 'tno_event_ticket_scanner_admin_key';
const LS_STAFF = 'tno_event_ticket_scanner_staff_id';

const styles = {
  root: {
    minHeight: '100vh',
    background: '#07070c',
    color: '#f0f2f6',
    padding: 16,
    boxSizing: 'border-box',
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  title: { fontSize: 20, fontWeight: 700, marginBottom: 8 },
  hint: { fontSize: 13, opacity: 0.65, marginBottom: 16, lineHeight: 1.45 },
  videoWrap: {
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    maxWidth: 480,
    margin: '0 auto 16px',
    background: '#111',
    aspectRatio: '4/3',
  },
  video: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  hiddenCanvas: { display: 'none' },
  panel: {
    maxWidth: 480,
    margin: '0 auto',
    padding: 16,
    borderRadius: 14,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(0,0,0,0.35)',
    color: '#fff',
    fontSize: 15,
    marginBottom: 10,
  },
  btn: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 12,
    border: 'none',
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    marginTop: 8,
  },
  resultGreen: {
    marginTop: 20,
    padding: 24,
    borderRadius: 16,
    textAlign: 'center',
    background: 'rgba(34,197,94,0.18)',
    border: '2px solid rgba(74,222,128,0.55)',
    color: '#bbf7d0',
  },
  resultRed: {
    marginTop: 20,
    padding: 24,
    borderRadius: 16,
    textAlign: 'center',
    background: 'rgba(239,68,68,0.15)',
    border: '2px solid rgba(248,113,113,0.5)',
    color: '#fecaca',
  },
  big: { fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: 800, marginTop: 8 },
};

function TicketScannerPage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const [adminKey, setAdminKey] = useState(() => {
    if (typeof window === 'undefined') return '';
    return (
      window.localStorage.getItem(LS_KEY) ||
      process.env.REACT_APP_EVENT_TICKET_ADMIN_KEY ||
      ''
    );
  });
  const [staffId, setStaffId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(LS_STAFF) || '';
  });
  const [manualToken, setManualToken] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [busy, setBusy] = useState(false);
  /** Tras guardar (o si ya había clave guardada), solo cámara + token manual. */
  const [showSettings, setShowSettings] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !window.localStorage.getItem(LS_KEY);
  });

  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  const runCheckin = useCallback(
    async (token) => {
      const t = (token || '').trim();
      if (!t) return;
      if (!isEventTicketApiConfigured()) {
        setLastResult({
          ui: 'red',
          message:
            'No se pudo resolver la URL del API de tickets (entorno sin window).',
        });
        return;
      }
      setBusy(true);
      setLastResult(null);
      try {
        const { ok, data } = await postCheckin(
          t,
          adminKey.trim(),
          staffId.trim()
        );
        if (data?.message && (data.ui === 'green' || data.ui === 'red')) {
          setLastResult({
            ui: data.ui,
            message: data.message,
            state: data.state,
            attendee_name: data.attendee_name,
            access_type: data.access_type,
            checkin_time: data.checkin_time,
          });
          return;
        }
        if (!ok) {
          setLastResult({
            ui: 'red',
            message:
              data?.message ||
              (data?.error === 'invalid_admin_key'
                ? 'Clave admin inválida'
                : 'Error de red o servidor'),
          });
          return;
        }
        setLastResult({
          ui: data.ui === 'green' ? 'green' : 'red',
          message: data.message,
          state: data.state,
          attendee_name: data.attendee_name,
          access_type: data.access_type,
          checkin_time: data.checkin_time,
        });
      } catch (e) {
        const msg =
          e instanceof TypeError
            ? 'No hubo respuesta (red, CORS o certificado). Revisa conexión y dominio.'
            : e instanceof Error
              ? e.message
              : 'Error al validar';
        setLastResult({ ui: 'red', message: msg });
      } finally {
        setBusy(false);
      }
    },
    [adminKey, staffId]
  );

  const tick = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !cameraOn) return;

    const ctx = canvas.getContext('2d');
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });
      if (code && code.data) {
        const text = code.data.trim();
        if (
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            text
          )
        ) {
          stopCamera();
          void runCheckin(text);
          return;
        }
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [cameraOn, runCheckin, stopCamera]);

  useEffect(() => {
    if (!cameraOn) return undefined;
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cameraOn, tick]);

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setLastResult({
        ui: 'red',
        message: 'Este navegador no permite cámara desde aquí.',
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
      setLastResult(null);
    } catch (e) {
      setLastResult({
        ui: 'red',
        message: 'No se pudo abrir la cámara. Usa pegar token o permisos.',
      });
    }
  };

  useEffect(() => () => stopCamera(), [stopCamera]);

  const saveConfig = () => {
    if (typeof window === 'undefined' || !adminKey.trim()) return;
    window.localStorage.setItem(LS_KEY, adminKey.trim());
    window.localStorage.setItem(LS_STAFF, staffId.trim());
    setShowSettings(false);
  };

  if (!isEventTicketApiConfigured()) {
    return (
      <div style={styles.root}>
        <div style={styles.title}>Check-in TNO</div>
        <p style={styles.hint}>
          En producción se usa automáticamente{' '}
          <code style={{ color: '#93c5fd' }}>/ticket-api</code> en el mismo dominio.
          En desarrollo local define{' '}
          <code style={{ color: '#93c5fd' }}>REACT_APP_EVENT_TICKET_SERVICE_URL</code>{' '}
          (p. ej. <code style={{ color: '#93c5fd' }}>http://localhost:5055</code>).
        </p>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.title}>Check-in · escáner</div>
      <p style={styles.hint}>
        Herramienta interna. Escanea el QR de la credencial o pega el token UUID.
        La clave admin no debe estar en builds públicos sin protección.
      </p>

      {showSettings && (
        <>
          <div style={styles.panel}>
            <label style={{ fontSize: 12, opacity: 0.7 }}>X-Admin-Api-Key</label>
            <input
              style={styles.input}
              type="password"
              autoComplete="off"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Clave del servicio de tickets"
            />
            <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginTop: 14 }}>
              Staff ID (opcional)
            </label>
            <input
              style={styles.input}
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              placeholder="ej. nombre o código de staff"
            />
            <button
              type="button"
              style={{ ...styles.btn, background: '#334155', color: '#fff' }}
              onClick={saveConfig}
            >
              Guardar en este dispositivo
            </button>
          </div>
          <div style={{ height: 16 }} />
        </>
      )}

      {!showSettings && (
        <p style={{ ...styles.hint, marginTop: 0, marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#93c5fd',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 13,
              padding: 0,
            }}
          >
            Cambiar clave o staff
          </button>
        </p>
      )}

      <div style={styles.videoWrap}>
        <video ref={videoRef} style={styles.video} playsInline muted />
        <canvas ref={canvasRef} style={styles.hiddenCanvas} />
      </div>

      <button
        type="button"
        style={{ ...styles.btn, background: '#2563eb', color: '#fff', maxWidth: 480, display: 'block', margin: '0 auto 12px' }}
        onClick={cameraOn ? stopCamera : startCamera}
      >
        {cameraOn ? 'Detener cámara' : 'Activar cámara'}
      </button>

      <div style={{ ...styles.panel, marginTop: 8 }}>
        <label style={{ fontSize: 12, opacity: 0.7 }}>Token manual</label>
        <input
          style={styles.input}
          value={manualToken}
          onChange={(e) => setManualToken(e.target.value)}
          placeholder="UUID del QR"
        />
        <button
          type="button"
          disabled={busy}
          style={{
            ...styles.btn,
            background: busy ? '#475569' : '#7c3aed',
            color: '#fff',
          }}
          onClick={() => runCheckin(manualToken)}
        >
          {busy ? 'Validando…' : 'Validar token'}
        </button>
      </div>

      {lastResult && (
        <div
          style={
            lastResult.ui === 'green' ? styles.resultGreen : styles.resultRed
          }
        >
          <div>{lastResult.message}</div>
          {lastResult.attendee_name && (
            <div style={styles.big}>{lastResult.attendee_name}</div>
          )}
          {lastResult.access_type && (
            <div style={{ marginTop: 8, opacity: 0.85 }}>
              {lastResult.access_type}
            </div>
          )}
          {lastResult.checkin_time && (
            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.8 }}>
              {new Date(lastResult.checkin_time).toLocaleString('es-VE')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TicketScannerPage;
